import React, { useState, useRef, useCallback, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useSummer2026 } from '../context/Summer2026Context';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Search, X, ArrowLeft, Clock, LogOut as LogOutIcon,
  CheckCircle, AlertTriangle, AlertCircle, Pen, Save, RefreshCw, Mail, Settings,
  Download, FileText, ChevronDown, ChevronUp, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  startNotificationSequence,
  cancelPendingReminders,
  rehydrateNotifications,
  getNotificationStatus,
  notifySnapshot,
} from '../utils/emailNotificationService';
import { getHeadCounselorEmail } from '../utils/headCounselorEmails';
import { getEasternToday, getEasternTimeString } from '../utils/dateUtils';
import { CamperAvatar } from './CamperAvatar';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

// Parse a pickup time ("2:00 PM", "11:10 AM", "330", "1:00pm") into minutes since
// midnight for sorting. No am/pm → camp-hours heuristic: 1–5 = PM. Unparseable → end.
const pickupMinutes = (t) => {
  const s = String(t || '').trim().toLowerCase();
  if (!s) return 24 * 60;
  const ap = (s.match(/[ap]\.?m\.?/) || [])[0];
  let h, min;
  if (/^\d{3,4}$/.test(s)) {
    h = parseInt(s.slice(0, s.length - 2), 10);
    min = parseInt(s.slice(-2), 10);
  } else {
    const m = s.match(/(\d{1,2})(?::(\d{2}))?/);
    if (!m) return 24 * 60;
    h = parseInt(m[1], 10);
    min = m[2] ? parseInt(m[2], 10) : 0;
  }
  if (isNaN(h) || isNaN(min) || h > 23 || min > 59) return 24 * 60;
  if (ap && ap.startsWith('p') && h < 12) h += 12;
  if (ap && ap.startsWith('a') && h === 12) h = 0;
  if (!ap && h >= 1 && h <= 5) h += 12;
  return h * 60 + min;
};

// Sort "First Last" names by last name, then first name
const byLastName = (aName, bName) => {
  const ap = (aName || '').trim().split(/\s+/);
  const bp = (bName || '').trim().split(/\s+/);
  const aLast = ap.length > 1 ? ap.slice(1).join(' ') : ap[0] || '';
  const bLast = bp.length > 1 ? bp.slice(1).join(' ') : bp[0] || '';
  return aLast.localeCompare(bLast) || (ap[0] || '').localeCompare(bp[0] || '');
};

// Format "First Last" as "Last, First"
const lastFirst = (name) => {
  const p = (name || '').trim().split(/\s+/);
  return p.length > 1 ? `${p.slice(1).join(' ')}, ${p[0]}` : (name || '');
};

// Normalize a name for matching: lowercase, letters/spaces only, collapsed spaces
const normName = (s) => String(s || '').toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim();

// Resolve an incoming dismissal to a roster camper. Exact id/name first, then a
// unique nickname-tolerant fallback (same last name + first names sharing a
// 3-letter prefix, e.g. Danny/Daniel). Ambiguous → null (staff searches manually).
const findRosterCamper = (roster, d) => {
  const target = normName(d.camper_name);
  if (!target) return null;
  const exact = roster.find(c =>
    (d.camper_id && String(c.personId) === String(d.camper_id)) ||
    normName(`${c.firstName} ${c.lastName}`) === target
  );
  if (exact) return exact;
  const parts = target.split(' ');
  if (parts.length < 2) return null;
  const first = parts[0];
  const last = parts.slice(1).join(' ');
  const candidates = roster.filter(c => {
    const cf = normName(c.firstName);
    const cl = normName(c.lastName);
    if (cl !== last) return false;
    const p = Math.min(3, first.length, cf.length);
    return p >= 3 && (cf.startsWith(first.slice(0, p)) || first.startsWith(cf.slice(0, p)));
  });
  return candidates.length === 1 ? candidates[0] : null;
};

const SESSION_COLORS = {
  full:   { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Full' },
  half1:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Half 1' },
  half2:  { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Half 2' },
  '6week': { bg: 'bg-orange-100', text: 'text-orange-800', label: '6 Wk' },
  flex20: { bg: 'bg-pink-100',   text: 'text-pink-800',   label: 'Flex 20' },
  flex25: { bg: 'bg-teal-100',   text: 'text-teal-800',   label: 'Flex 25' },
  flex30: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Flex 30' },
};

export default function Summer2026FrontDesk({ onLogout }) {
  const { roster, selectedDate, dates, setSelectedDate, loading, syncRoster, setAttendanceForCamper, fetchBusAttendance } = useSummer2026();
  const [search, setSearch] = useState('');
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [logType, setLogType] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [pickupInfo, setPickupInfo] = useState('');
  const [travelInfo, setTravelInfo] = useState('');
  const [authorizedPeople, setAuthorizedPeople] = useState([]);
  const [newAuthPerson, setNewAuthPerson] = useState('');
  const [savingAuthPerson, setSavingAuthPerson] = useState(false);
  const [additionalPeople, setAdditionalPeople] = useState('');
  const [additionalPeopleSaved, setAdditionalPeopleSaved] = useState(null);
  const [savingAdditional, setSavingAdditional] = useState(false);
  const [expectedTime, setExpectedTime] = useState('');
  const [expectedTimeSaved, setExpectedTimeSaved] = useState(null);
  const [savingTime, setSavingTime] = useState(false);
  const [savedNotes, setSavedNotes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [notifStatus, setNotifStatus] = useState(null); // inline notification feedback
  const [showSettings, setShowSettings] = useState(false);
  const [showFdDatePicker, setShowFdDatePicker] = useState(false);
  const [resendKeyInput, setResendKeyInput] = useState('');
  const [showResendKey, setShowResendKey] = useState(false);
  const [earlyDismissals, setEarlyDismissals] = useState([]);
  const [incomingDismissals, setIncomingDismissals] = useState([]);
  const [incomingFetchError, setIncomingFetchError] = useState(false);
  const [hiddenDismissalIds, setHiddenDismissalIds] = useState(new Set());
  const [editingDismissalId, setEditingDismissalId] = useState(null);
  const [editDismissalTime, setEditDismissalTime] = useState('');
  const [activeIncomingDismissalId, setActiveIncomingDismissalId] = useState(null);
  const [edPanelOpen, setEdPanelOpen] = useState(true);
  const [edLoading, setEdLoading] = useState(false);
  const [dismissalDuration, setDismissalDuration] = useState('today');
  const [customDates, setCustomDates] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [overrideTime, setOverrideTime] = useState('');
  const [overrideDate, setOverrideDate] = useState('');
  const sigRef = useRef(null);

  // Rehydrate pending notification timers on mount
  useEffect(() => {
    const handleStatus = (status) => {
      // Global status handler for rehydrated notifications
      console.log('Rehydrated notification status:', status);
    };
    rehydrateNotifications(handleStatus);
  }, []);

  // Force sync roster when Front Desk mounts if roster is empty or stale
  useEffect(() => {
    if (roster.length === 0 && !loading) {
      syncRoster();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch early dismissals for today
  const fetchEarlyDismissals = useCallback(async () => {
    if (!selectedDate) return;
    setEdLoading(true);
    try {
      const res = await safeFetch(`${API}/summer2026/frontdesk/logs?date=${selectedDate}&logType=early_dismissal`);
      const data = await res.json();
      if (data.success) {
        setEarlyDismissals(data.logs || []);
      }
    } catch (err) {
      console.error('Fetch early dismissals error:', err);
    } finally {
      setEdLoading(false);
    }
  }, [selectedDate]);

  // Load early dismissals on mount and when date changes
  useEffect(() => {
    fetchEarlyDismissals();
  }, [fetchEarlyDismissals]);

  // Poll Camper Snapshot for incoming early dismissals every 30s
  // MERGE strategy: keep existing local entries, add new ones from API, never drop entries
  // unless they were explicitly cancelled by the user (tracked in hiddenDismissalIds)
  // Always passes the currently viewed date so backend uses the same Eastern date
  const fetchIncomingDismissals = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const res = await safeFetch(`${API}/summer2026/snapshot/early-dismissals?date=${selectedDate}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setIncomingFetchError(!!data.fetch_error);
      if (data.early_dismissals) {
        setIncomingDismissals(prev => {
          const apiEntries = data.early_dismissals.filter(d => !d.dismissed && d.id);
          // Build a map of existing entries by id — skip entries with no id
          const existingMap = new Map(prev.filter(d => d.id).map(d => [d.id, d]));
          // Add/update from API, but keep any existing entries that API no longer returns
          apiEntries.forEach(d => {
            existingMap.set(d.id, d); // update with latest data from API
          });
          return Array.from(existingMap.values());
        });
      }
    } catch (err) {
      setIncomingFetchError(true);
      console.warn('Incoming dismissals poll error:', err);
    }
  }, [selectedDate]);

  const editDismissalLocation = async (d) => {
    const val = window.prompt('Where is this camper right now? (leave empty to reset to the scheduled location)', d.location_is_override ? (d.scheduled_location || '') : '');
    if (val === null) return;
    const loc = val.trim();
    try {
      await safeFetch(`${API}/summer2026/snapshot/dismissal-location`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: d.id, location: loc }),
      });
      setIncomingDismissals(prev => prev.map(x => x.id === d.id
        ? { ...x, scheduled_location: loc || null, location_is_override: !!loc }
        : x));
      if (!loc) fetchIncomingDismissals();
    } catch (err) {
      console.warn('Location override:', err);
    }
  };

  useEffect(() => {
    fetchIncomingDismissals();
    const interval = setInterval(fetchIncomingDismissals, 30000);
    return () => clearInterval(interval);
  }, [fetchIncomingDismissals]);

  // Calculate recurring dates based on duration selection
  const getRecurringDates = () => {
    if (dismissalDuration === 'today') return [selectedDate];
    if (dismissalDuration === 'custom') return customDates;

    // dates = all camp days from context, sorted ascending
    const startIdx = dates.indexOf(selectedDate);
    if (startIdx === -1) return [selectedDate];

    const weeksMap = { '1week': 5, '2weeks': 10, '3weeks': 15, '4weeks': 20 };
    if (weeksMap[dismissalDuration]) {
      return dates.slice(startIdx, startIdx + weeksMap[dismissalDuration]);
    }
    if (dismissalDuration === 'summer') {
      return dates.slice(startIdx);
    }
    return [selectedDate];
  };

  // Ensure selectedDate is set once dates are available
  useEffect(() => {
    if (dates.length > 0 && !selectedDate) {
      const today = getEasternToday();
      if (dates.includes(today)) {
        setSelectedDate(today);
      } else {
        const upcoming = dates.find(d => d >= today);
        setSelectedDate(upcoming || dates[dates.length - 1]);
      }
    }
  }, [dates, selectedDate, setSelectedDate]);

  const handleRefreshRoster = async () => {
    setRefreshing(true);
    await syncRoster();
    setRefreshing(false);
  };

  // With Snapshot, roster is already per-date — no need to filter by daysAttending
  const todayCampers = roster
    .sort((a, b) => {
      const last = a.lastName.localeCompare(b.lastName);
      return last !== 0 ? last : a.firstName.localeCompare(b.firstName);
    });

  const q = search.trim().toLowerCase();
  const filtered = q
    ? todayCampers.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
    : todayCampers;

  // Fetch authorized pickup when camper is selected
  const fetchPickup = useCallback(async (personId, cmPersonId) => {
    const cmId = cmPersonId || personId;
    try {
      const res = await safeFetch(`${API}/summer2026/frontdesk/authorized-pickup?personIds=${cmId}`);
      const data = await res.json();
      if (data.success) {
        setPickupInfo(data.pickupInfo[String(cmId)] || '');
        setTravelInfo(data.travelInfo[String(cmId)] || '');
      }
    } catch {
      setPickupInfo('');
      setTravelInfo('');
    }
    // Also fetch admin-added authorized people (uses personId/UUID)
    try {
      const res = await safeFetch(`${API}/summer2026/authorized-people?person_id=${personId}`);
      const data = await res.json();
      if (data.success) setAuthorizedPeople(data.people);
      else setAuthorizedPeople([]);
    } catch {
      setAuthorizedPeople([]);
    }
  }, []);

  // Fetch existing saved notes for camper
  const fetchNotes = useCallback(async (personId) => {
    try {
      const today = getEasternToday();
      const res = await safeFetch(`${API}/summer2026/frontdesk/camper-notes?personId=${personId}&date=${today}`);
      const data = await res.json();
      if (data.success) {
        setSavedNotes(data.notes);
        // Pre-fill fields from latest saved notes
        const latestAdditional = data.notes.find(n => n.field === 'additionalPeople');
        const latestTime = data.notes.find(n => n.field === 'expectedTime');
        if (latestAdditional) {
          setAdditionalPeople(latestAdditional.value);
          setAdditionalPeopleSaved(latestAdditional.savedAt);
        }
        if (latestTime) {
          setExpectedTime(latestTime.value);
          setExpectedTimeSaved(latestTime.savedAt);
        }
      }
    } catch {
      setSavedNotes([]);
    }
  }, []);

  const handleSelectCamper = (camper) => {
    setSelectedCamper(camper);
    setLogType(null);
    setSignerName('');
    setNotes('');
    setSubmitted(null);
    setPickupInfo('');
    setTravelInfo('');
    setAdditionalPeople('');
    setAdditionalPeopleSaved(null);
    setExpectedTime('');
    setExpectedTimeSaved(null);
    setSavedNotes([]);
    setNotifStatus(null);
    setOverrideTime('');
    setOverrideDate('');
    fetchPickup(camper.personId, camper.cmPersonId);
    fetchNotes(camper.personId);
    setAuthorizedPeople([]);
    setNewAuthPerson('');

    // Load pickup time from localStorage
    const today = selectedDate || getEasternToday();
    const savedPickup = localStorage.getItem(`pickup_${camper.personId}_${today}`);
    if (savedPickup) {
      setExpectedTime(savedPickup);
    }
    // Load existing notification status
    const ns = getNotificationStatus(camper.personId, today);
    if (ns) {
      if (ns.email1Sent && ns.email2Sent && ns.email3Sent) {
        setNotifStatus({ type: 'all_sent' });
      } else if (ns.email1Sent && ns.email2Sent) {
        setNotifStatus({ type: 'email_success', reminderNum: 2 });
      } else if (ns.email1Sent) {
        setNotifStatus({ type: 'email_success', reminderNum: 1 });
      }
    }
  };

  const handleBack = () => {
    setSelectedCamper(null);
    setLogType(null);
    setSubmitted(null);
    setDismissalDuration('today');
    setCustomDates([]);
    setShowDatePicker(false);
    setActiveIncomingDismissalId(null);
  };


  // Add authorized pickup person (persists all summer)
  const handleAddAuthPerson = async () => {
    if (!newAuthPerson.trim() || !selectedCamper) return;
    setSavingAuthPerson(true);
    try {
      const res = await safeFetch(`${API}/summer2026/authorized-people`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ person_id: String(selectedCamper.personId), name: newAuthPerson.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setNewAuthPerson('');
        fetchPickup(selectedCamper.personId);
      }
    } catch (e) { console.error(e); }
    finally { setSavingAuthPerson(false); }
  };

  // Remove authorized pickup person
  const handleRemoveAuthPerson = async (name) => {
    if (!selectedCamper) return;
    try {
      await safeFetch(`${API}/summer2026/authorized-people?person_id=${selectedCamper.personId}&name=${encodeURIComponent(name)}`, { method: 'DELETE' });
      fetchPickup(selectedCamper.personId);
    } catch (e) { console.error(e); }
  };


  // Save additional people note
  const handleSaveAdditionalPeople = async () => {
    if (!additionalPeople.trim() || !selectedCamper) return;
    setSavingAdditional(true);
    try {
      const res = await safeFetch(`${API}/summer2026/frontdesk/camper-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personId: selectedCamper.personId,
          camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
          field: 'additionalPeople',
          value: additionalPeople.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setAdditionalPeopleSaved(data.note.savedAt);
      }
    } catch (err) {
      console.error('Save additional people error:', err);
    } finally {
      setSavingAdditional(false);
    }
  };

  // Save expected time note AND trigger email notification sequence
  const handleSaveExpectedTime = async () => {
    if (!selectedCamper) return;
    const today = selectedDate || getEasternToday();
    const timeValue = expectedTime.trim();

    // Handle cancellation: if time is cleared, cancel pending reminders
    if (!timeValue) {
      cancelPendingReminders(selectedCamper.personId, today);
      localStorage.removeItem(`pickup_${selectedCamper.personId}_${today}`);
      setNotifStatus(null);
      setExpectedTimeSaved(null);
      return;
    }

    setSavingTime(true);
    try {
      // Save to backend as before
      const res = await safeFetch(`${API}/summer2026/frontdesk/camper-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personId: selectedCamper.personId,
          camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
          field: 'expectedTime',
          value: timeValue,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setExpectedTimeSaved(data.note.savedAt);
      }

      // Persist pickup time to localStorage
      localStorage.setItem(`pickup_${selectedCamper.personId}_${today}`, timeValue);

      // Get existing dismissal time from notes (if any)
      const dismissalNote = savedNotes.find(n => n.field === 'dismissalTime');
      const dismissalTime = dismissalNote ? dismissalNote.value : '';

      // Start notification sequence (cancels any existing one first)
      const started = startNotificationSequence(
        selectedCamper,
        timeValue,
        dismissalTime,
        today,
        (status) => {
          setNotifStatus(status);
        }
      );

      if (!started) {
        // API key missing — still fire webhook to Snapshot
        setNotifStatus({ type: 'no_api_key' });
      }

      // Backend decides: send now or schedule for future
      safeFetch(`${API}/summer2026/notify-or-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'early_dismissal',
          camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
          groupCode: selectedCamper.bunkCode,
          groupName: selectedCamper.bunkName,
          date: today,
          dismissalTime: timeValue,
          pickupArrivalTime: timeValue,
          pickupBy: additionalPeople.trim() || '',
          camperId: String(selectedCamper.personId),
        }),
      }).catch(err => console.warn('Notify/schedule (non-blocking):', err));
    } catch (err) {
      console.error('Save expected time error:', err);
    } finally {
      setSavingTime(false);
    }
  };

  const handleSubmit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    if (!signerName.trim()) return;

    setSubmitting(true);
    try {
      const signatureData = sigRef.current.toDataURL('image/png');
      const isRecurring = logType === 'early_dismissal' && dismissalDuration !== 'today';

      if (isRecurring) {
        const recurDates = getRecurringDates();
        const res = await safeFetch(`${API}/summer2026/frontdesk/recurring-dismissal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            personId: selectedCamper.personId,
            camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
            bunkCode: selectedCamper.bunkCode,
            bunkName: selectedCamper.bunkName,
            signerName: signerName.trim(),
            signatureData,
            notes: notes.trim(),
            additionalPeople: additionalPeople.trim(),
            expectedTime: expectedTime.trim(),
            dates: recurDates,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setSubmitted({ ...data.log, _recurringCount: data.datesCount });
          fetchEarlyDismissals();
          // Bug 4 fix: Remove from incoming banner after sign-out (recurring)
          if (activeIncomingDismissalId) {
            setHiddenDismissalIds(prev => new Set([...prev, `${activeIncomingDismissalId}_${selectedDate}`]));
            setIncomingDismissals(prev => prev.filter(x => x.id !== activeIncomingDismissalId));
            // Mark as completed in backend DB so it doesn't reappear on refresh
            safeFetch(`${API}/summer2026/snapshot/dismiss-incoming?id=${activeIncomingDismissalId}`, { method: 'DELETE' })
              .catch(err => console.warn('Dismiss-incoming (non-blocking):', err));
            setActiveIncomingDismissalId(null);
          }
          // Fire webhook to Camper Snapshot for each recurring date
          recurDates.forEach(d => {
            // Backend decides: send now (today) or schedule for future
            safeFetch(`${API}/summer2026/notify-or-schedule`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventType: 'early_dismissal',
                camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
                groupCode: selectedCamper.bunkCode,
                groupName: selectedCamper.bunkName,
                date: d,
                dismissalTime: expectedTime.trim(),
                pickupArrivalTime: expectedTime.trim(),
                pickupBy: signerName.trim(),
                camperId: String(selectedCamper.personId),
              }),
            }).catch(err => console.warn('Notify/schedule (non-blocking):', err));
          });
        }
      } else {
        const res = await safeFetch(`${API}/summer2026/frontdesk/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            personId: selectedCamper.personId,
            camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
            bunkCode: selectedCamper.bunkCode,
            bunkName: selectedCamper.bunkName,
            logType,
            signerName: signerName.trim(),
            signatureData,
            notes: notes.trim(),
            additionalPeople: additionalPeople.trim(),
            expectedTime: expectedTime.trim(),
            overrideTime: overrideTime || '',
            overrideDate: overrideDate || '',
          }),
        });
        const data = await res.json();
        if (data.success) {
          setSubmitted(data.log);
          const camperDate = overrideDate || selectedDate || getEasternToday();
          const logTime = overrideTime || getEasternTimeString();
          // Bug 4 fix: Remove from incoming banner after sign-out
          if (activeIncomingDismissalId) {
            setHiddenDismissalIds(prev => new Set([...prev, `${activeIncomingDismissalId}_${selectedDate}`]));
            setIncomingDismissals(prev => prev.filter(x => x.id !== activeIncomingDismissalId));
            // Mark as completed in backend DB so it doesn't reappear on refresh
            safeFetch(`${API}/summer2026/snapshot/dismiss-incoming?id=${activeIncomingDismissalId}`, { method: 'DELETE' })
              .catch(err => console.warn('Dismiss-incoming (non-blocking):', err));
            setActiveIncomingDismissalId(null);
          }
          // Fire-and-forget: don't let webhook/notification errors prevent confirmation
          try {
            if (logType === 'early_dismissal') {
              fetchEarlyDismissals();
              // Always notify/schedule so the head counselor gets the instant message.
              // alreadySigned=true (today-signed walk-up) tells the backend to skip
              // only the Snapshot incoming-banner notify, keeping the Staff Guide message.
              safeFetch(`${API}/summer2026/notify-or-schedule`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  eventType: 'early_dismissal',
                  camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
                  groupCode: selectedCamper.bunkCode,
                  groupName: selectedCamper.bunkName,
                  date: camperDate,
                  dismissalTime: logTime,
                  pickupArrivalTime: expectedTime.trim(),
                  pickupBy: signerName.trim(),
                  camperId: String(selectedCamper.personId),
                  alreadySigned: camperDate === getEasternToday(),
                }),
              }).catch(err => console.warn('Notify/schedule (non-blocking):', err));
            }
            if (logType === 'late_arrival') {
              setAttendanceForCamper(selectedCamper.personId, camperDate, {
                status: 'late',
                lateTime: logTime,
              });
              // Update bus attendance to "present" (backend already does this too, but refresh frontend)
              safeFetch(`${API}/summer2026/bus-attendance/mark`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  first_name: selectedCamper.firstName,
                  last_name: selectedCamper.lastName,
                  status: 'present',
                  bus_number: '',
                  date: camperDate,
                  camper_id: String(selectedCamper.personId),
                }),
              }).then(() => {
                if (typeof fetchBusAttendance === 'function') fetchBusAttendance(camperDate);
              }).catch(err => console.warn('Bus mark for late arrival (non-blocking):', err));

              // Notify Camper Snapshot with arrival time
              notifySnapshot({
                eventType: 'late_arrival',
                groupCode: selectedCamper.bunkCode,
                camperName: `${selectedCamper.firstName} ${selectedCamper.lastName}`,
                arrivalTime: logTime,
                signerName: signerName.trim(),
                notes: notes.trim(),
                date: camperDate,
              });

              // Send email notification to head counselor with arrival time
              const groupInfo = getHeadCounselorEmail(selectedCamper.bunkCode);
              if (groupInfo && groupInfo.email) {
                const camperFullName = `${selectedCamper.firstName} ${selectedCamper.lastName}`;
                const dateDisplay = new Date(camperDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                const emailBody = `Rolling River Day Camp — Late Arrival Notice\n\nCamper: ${camperFullName}\nGroup: ${groupInfo.name} (${selectedCamper.bunkCode})\nDate: ${dateDisplay}\nArrival Time: ${logTime}\n${signerName.trim() ? `Signed in by: ${signerName.trim()}` : ''}\n${notes.trim() ? `Notes: ${notes.trim()}` : ''}\n\n${camperFullName} has arrived late and has been checked in. Please update your group roster.\n\nThank you,\nRolling River Attendance System`;
                safeFetch(`${API}/send-email`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    apiKey: localStorage.getItem('resend_api_key') || 're_BeuYp4FZ_FaMNTJZzXqTFvPHX7dcKf2GU',
                    from: 'attendance@campersnapshot.com',
                    to: groupInfo.email,
                    subject: `Late Arrival: ${camperFullName} — ${logTime}`,
                    text: emailBody,
                  }),
                }).catch(err => console.warn('Late arrival email (non-blocking):', err));
              }
            }
          } catch (webhookErr) {
            console.warn('Webhook/notification error (non-blocking):', webhookErr);
          }
        } else {
          console.error('Front desk log failed:', data);
        }
      }
    } catch (err) {
      console.error('Front desk log error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const timeStr = overrideTime || getEasternTimeString();
  const effectiveDate = overrideDate || (selectedDate || getEasternToday());
  const dateDisplay = new Date(effectiveDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // ── Confirmation Screen ──
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <div className="bg-[#0055A4] py-4 px-4 text-center">
          <h1 className="text-white text-xl font-bold font-['Fredoka']">Front Desk</h1>
        </div>
        <div className="max-w-lg mx-auto px-4 py-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-lg border p-6 text-center"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] font-['Fredoka'] mb-2">
              {submitted.logType === 'late_arrival' ? 'Late Arrival' : 'Early Dismissal'} Recorded
            </h2>
            <p className="text-lg text-[#475569] mb-1">{submitted.camperName}</p>
            <p className="text-sm text-[#475569] mb-1">{submitted.bunkCode} — {submitted.bunkName}</p>
            {submitted._recurringCount > 1 && (
              <Badge className="bg-teal-100 text-teal-800 px-3 py-1 text-sm mt-1" data-testid="recurring-badge">
                Recurring — {submitted._recurringCount} days scheduled
              </Badge>
            )}
            <div className="flex justify-center gap-4 my-4">
              <Badge className={submitted.logType === 'late_arrival'
                ? 'bg-amber-100 text-amber-800 px-4 py-2 text-base'
                : 'bg-teal-100 text-teal-800 px-4 py-2 text-base'}>
                {submitted.logType === 'late_arrival' ? 'Late Arrival' : 'Early Dismissal'}
              </Badge>
              <Badge className="bg-gray-100 text-gray-800 px-4 py-2 text-base">{submitted.time}</Badge>
            </div>
            <p className="text-sm text-[#475569]">Signed by: {submitted.signerName}</p>
            {submitted.additionalPeople && (
              <p className="text-xs text-[#475569] mt-2">Additional People: {submitted.additionalPeople}</p>
            )}
            {submitted.expectedTime && (
              <p className="text-xs text-[#475569] mt-1">Expected Time: {submitted.expectedTime}</p>
            )}
            <img src={submitted.signatureData} alt="Signature" className="mx-auto mt-4 border rounded-lg max-h-24" />
            <Button
              className="mt-6 w-full bg-[#0055A4] hover:bg-[#003F7D] text-white rounded-xl h-12 text-base"
              onClick={handleBack}
              data-testid="frontdesk-done-btn"
            >
              Done — Back to Camper List
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Signature Form ──
  if (selectedCamper && logType) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <div className="bg-[#0055A4] py-4 px-4">
          <div className="max-w-lg mx-auto flex items-center">
            <button onClick={() => setLogType(null)} className="text-white mr-3">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-white text-xl font-bold font-['Fredoka']">
              {logType === 'late_arrival' ? 'Late Arrival' : 'Early Dismissal'}
            </h1>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
          <div className="bg-white rounded-2xl shadow border p-4">
            <p className="text-xl font-bold text-[#0F172A]">{selectedCamper.firstName} {selectedCamper.lastName}</p>
            <p className="text-sm text-[#475569]">{selectedCamper.bunkCode} — {selectedCamper.bunkName}</p>
          </div>

          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#0055A4] shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-[#0F172A]">{timeStr}</p>
                <p className="text-xs text-[#475569]">{dateDisplay}</p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <input
                type="time"
                value={overrideTime ? (() => {
                  const m = overrideTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
                  if (!m) return '';
                  let h = parseInt(m[1]);
                  const ampm = m[3].toUpperCase();
                  if (ampm === 'PM' && h !== 12) h += 12;
                  if (ampm === 'AM' && h === 12) h = 0;
                  return `${String(h).padStart(2,'0')}:${m[2]}`;
                })() : ''}
                onChange={e => {
                  if (e.target.value) {
                    const [h, m] = e.target.value.split(':');
                    const hr = parseInt(h);
                    const ampm = hr >= 12 ? 'PM' : 'AM';
                    const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
                    setOverrideTime(`${hr12}:${m} ${ampm}`);
                  } else {
                    setOverrideTime('');
                  }
                }}
                className="flex-1 border rounded-lg px-2 py-1.5 text-sm bg-gray-50"
                data-testid="frontdesk-override-time"
              />
              <input
                type="date"
                value={overrideDate || effectiveDate}
                onChange={e => setOverrideDate(e.target.value)}
                className="flex-1 border rounded-lg px-2 py-1.5 text-sm bg-gray-50"
                data-testid="frontdesk-override-date"
              />
            </div>
            {(overrideTime || overrideDate) && (
              <button
                onClick={() => { setOverrideTime(''); setOverrideDate(''); }}
                className="text-xs text-[#0055A4] hover:underline"
              >
                Reset to current time/date
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <label className="text-sm font-semibold text-[#0F172A]">Name of Parent / Authorized Person</label>
            <Input
              data-testid="frontdesk-signer-name"
              value={signerName}
              onChange={e => setSignerName(e.target.value)}
              placeholder="Full name..."
              className="h-12 text-base rounded-xl"
            />
          </div>

          {/* Authorized for Pickup */}
          {(pickupInfo || travelInfo) && (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
              <p className="text-sm font-semibold text-amber-800 mb-2">Authorized for Pickup:</p>
              {travelInfo && <p className="text-sm text-amber-700 whitespace-pre-wrap">{travelInfo}</p>}
              {pickupInfo && travelInfo && <hr className="my-2 border-amber-200" />}
              {pickupInfo && <p className="text-sm text-amber-700 whitespace-pre-wrap">{pickupInfo}</p>}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <label className="text-sm font-semibold text-[#0F172A]">Notes (optional)</label>
            <Input
              data-testid="frontdesk-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="h-12 text-base rounded-xl"
            />
          </div>

          {/* Duration Picker — only for early dismissals */}
          {logType === 'early_dismissal' && (
            <div className="bg-white rounded-2xl shadow border p-4 space-y-3" data-testid="dismissal-duration-section">
              <label className="text-sm font-semibold text-[#0F172A]">Duration</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'today', label: 'Today Only' },
                  { id: '1week', label: '1 Week' },
                  { id: '2weeks', label: '2 Weeks' },
                  { id: '3weeks', label: '3 Weeks' },
                  { id: '4weeks', label: '4 Weeks' },
                  { id: 'summer', label: 'Whole Summer' },
                  { id: 'custom', label: 'Specific Days' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setDismissalDuration(opt.id);
                      if (opt.id === 'custom') setShowDatePicker(true);
                      else setShowDatePicker(false);
                    }}
                    data-testid={`duration-${opt.id}`}
                    className={`px-3 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                      dismissalDuration === opt.id
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-teal-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Date count preview for non-custom */}
              {dismissalDuration !== 'today' && dismissalDuration !== 'custom' && (
                <p className="text-xs text-teal-700 font-medium" data-testid="duration-date-count">
                  {getRecurringDates().length} camp day{getRecurringDates().length !== 1 ? 's' : ''} selected
                  {getRecurringDates().length > 0 && (
                    <span className="text-[#94A3B8] ml-1">
                      ({getRecurringDates()[0]} to {getRecurringDates()[getRecurringDates().length - 1]})
                    </span>
                  )}
                </p>
              )}

              {/* Custom date picker */}
              {dismissalDuration === 'custom' && showDatePicker && (
                <div className="space-y-2">
                  <p className="text-xs text-[#475569]">Select specific camp days:</p>
                  <div className="max-h-48 overflow-y-auto border rounded-xl p-2 bg-[#F8FAFC]">
                    <div className="grid grid-cols-2 gap-1.5">
                      {dates
                        .filter(d => d >= selectedDate)
                        .map(d => {
                          const dt = new Date(d + 'T12:00:00');
                          const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                          const isChecked = customDates.includes(d);
                          return (
                            <button
                              key={d}
                              onClick={() => {
                                setCustomDates(prev =>
                                  isChecked ? prev.filter(x => x !== d) : [...prev, d].sort()
                                );
                              }}
                              data-testid={`custom-date-${d}`}
                              className={`px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all text-left ${
                                isChecked
                                  ? 'bg-teal-100 text-teal-800 border-teal-400'
                                  : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-teal-200'
                              }`}
                            >
                              {isChecked ? '✓ ' : ''}{label}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                  <p className="text-xs text-teal-700 font-medium" data-testid="custom-date-count">
                    {customDates.length} day{customDates.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-[#0F172A] flex items-center gap-1">
                <Pen className="w-4 h-4" /> Signature
              </label>
              <button onClick={() => sigRef.current?.clear()} className="text-xs text-[#0055A4] hover:underline">Clear</button>
            </div>
            <div className="border-2 border-dashed border-[#CBD5E1] rounded-xl bg-gray-50">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0F172A"
                canvasProps={{
                  className: 'w-full',
                  style: { width: '100%', height: '200px' },
                  'data-testid': 'frontdesk-signature-pad',
                }}
              />
            </div>
            <p className="text-xs text-[#94A3B8] text-center">Sign above with your finger or stylus</p>
          </div>

          <Button
            className="w-full bg-[#0055A4] hover:bg-[#003F7D] text-white rounded-xl h-14 text-lg font-semibold disabled:opacity-50"
            onClick={handleSubmit}
            disabled={submitting || !signerName.trim() || (dismissalDuration === 'custom' && customDates.length === 0)}
            data-testid="frontdesk-submit-btn"
          >
            {submitting
              ? 'Saving...'
              : logType === 'late_arrival'
                ? 'Submit Late Arrival'
                : dismissalDuration !== 'today'
                  ? `Submit Early Dismissal (${dismissalDuration === 'custom' ? customDates.length : getRecurringDates().length} days)`
                  : 'Submit Early Dismissal'
            }
          </Button>
        </div>
      </div>
    );
  }

  // ── Camper Detail (type selection) ──
  if (selectedCamper) {
    const si = SESSION_COLORS[selectedCamper.sessionType] || SESSION_COLORS.full;
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <div className="bg-[#0055A4] py-4 px-4">
          <div className="max-w-lg mx-auto flex items-center">
            <button onClick={handleBack} className="text-white mr-3" data-testid="frontdesk-back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-white text-xl font-bold font-['Fredoka']">
              {selectedCamper.firstName} {selectedCamper.lastName}
            </h1>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
          <div className="bg-white rounded-2xl shadow border p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${si.bg} ${si.text}`}>
                {si.label}
              </span>
              <span className="text-sm text-[#475569]">{selectedCamper.bunkCode} — {selectedCamper.bunkName}</span>
            </div>
            <p className="text-sm text-[#475569]">{selectedCamper.division}</p>
            {/* Pickup time pill */}
            {(() => {
              const today = selectedDate || getEasternToday();
              const savedPickup = localStorage.getItem(`pickup_${selectedCamper.personId}_${today}`);
              if (savedPickup) {
                return (
                  <span
                    className="inline-flex items-center mt-2 px-3 py-1 rounded-full text-xs font-medium bg-[#0F172A] text-white"
                    data-testid="pickup-time-pill"
                  >
                    Pickup: {savedPickup}
                  </span>
                );
              }
              return null;
            })()}
          </div>

          {/* Authorized for Pickup — combined CampMinder + admin-added */}
          {(pickupInfo || travelInfo || authorizedPeople.length > 0) && (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4" data-testid="frontdesk-pickup-info">
              <p className="text-sm font-semibold text-amber-800 mb-2">Authorized for Pickup:</p>
              {travelInfo && <p className="text-sm text-amber-700 whitespace-pre-wrap">{travelInfo}</p>}
              {pickupInfo && <p className="text-sm text-amber-700 whitespace-pre-wrap">{pickupInfo}</p>}
              {authorizedPeople.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {authorizedPeople.map((p, i) => (
                    <p key={i} className="text-sm text-amber-700">{p.name}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Additional People with Save */}
          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <label className="text-sm font-semibold text-[#0F172A]">Additional People</label>
            <div className="flex gap-2">
              <Input
                data-testid="frontdesk-additional-people"
                value={additionalPeople}
                onChange={e => { setAdditionalPeople(e.target.value); setAdditionalPeopleSaved(null); }}
                placeholder="Add authorized people..."
                className="h-12 text-base rounded-xl flex-1"
              />
              <Button
                onClick={handleSaveAdditionalPeople}
                disabled={savingAdditional || !additionalPeople.trim()}
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4"
                data-testid="frontdesk-save-additional"
              >
                <Save className="w-4 h-4 mr-1" />
                {savingAdditional ? '...' : 'Save'}
              </Button>
            </div>
            {additionalPeopleSaved && (
              <p className="text-xs text-emerald-600" data-testid="additional-saved-timestamp">
                Saved: {additionalPeopleSaved}
              </p>
            )}
          </div>

          {/* Time Pickup Person Will Arrive (Called/Emailed) — sole trigger for email notifications */}
          <div className="bg-white rounded-2xl shadow border p-4 space-y-2">
            <label className="text-sm font-semibold text-[#0F172A]">Time Pickup Person Will Arrive (Called/Emailed)</label>
            <div className="flex gap-2">
              <Input
                data-testid="frontdesk-expected-time"
                value={expectedTime}
                onChange={e => { setExpectedTime(e.target.value); setExpectedTimeSaved(null); setNotifStatus(null); }}
                placeholder="2:15 PM"
                className="h-12 text-base rounded-xl flex-1"
              />
              <Button
                onClick={handleSaveExpectedTime}
                disabled={savingTime}
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4"
                data-testid="frontdesk-save-time"
              >
                <Mail className="w-4 h-4 mr-1" />
                {savingTime ? '...' : 'Save & Notify'}
              </Button>
            </div>
            {expectedTimeSaved && !notifStatus && (
              <p className="text-xs text-emerald-600" data-testid="time-saved-timestamp">
                Saved: {expectedTimeSaved}
              </p>
            )}
            {/* Head counselor email info */}
            {selectedCamper.bunkCode && (
              <p className="text-xs text-[#94A3B8]" data-testid="frontdesk-counselor-email">
                Notifies: {getHeadCounselorEmail(selectedCamper.bunkCode).email}
              </p>
            )}
            {/* Inline notification feedback */}
            {notifStatus && notifStatus.type === 'no_api_key' && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1" data-testid="notif-no-key-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">Email notifications are not configured. Please add your Resend API key in Settings.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'email_success' && notifStatus.reminderNum === 1 && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-2 mt-1" data-testid="notif-success-1">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-xs text-green-700">Head counselor notified. Reminders scheduled at 10 and 20 minutes.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'email_success' && notifStatus.reminderNum === 2 && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-2 mt-1" data-testid="notif-success-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-xs text-green-700">10-minute reminder sent.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'all_sent' && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-2 mt-1" data-testid="notif-all-sent">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-xs text-green-700">All notifications sent.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'email_success' && notifStatus.reminderNum === 3 && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-2 mt-1" data-testid="notif-success-3">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-xs text-green-700">All notifications sent.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'email_failure' && notifStatus.reminderNum === 1 && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2 mt-1" data-testid="notif-failure-1">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">Notification failed to send for {selectedCamper.firstName} {selectedCamper.lastName}. Check Settings.</p>
              </div>
            )}
            {notifStatus && notifStatus.type === 'email_failure' && notifStatus.reminderNum > 1 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1" data-testid="notif-failure-reminder">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">Reminder {notifStatus.reminderNum} failed to send for {selectedCamper.firstName} {selectedCamper.lastName}.</p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {/* Authorized Pickup - from CampMinder custom field + admin-added */}
          <div className="bg-amber-50 rounded-2xl border-2 border-amber-300 p-4" data-testid="authorized-pickup-card">
            <p className="text-sm font-bold text-amber-900 mb-2">Authorized for Pickup</p>
            {travelInfo && <p className="text-sm text-amber-800 whitespace-pre-wrap mb-1">{travelInfo}</p>}
            {pickupInfo && <p className="text-sm text-amber-800 whitespace-pre-wrap mb-1">{pickupInfo}</p>}
            {authorizedPeople.length > 0 && (
              <div className="space-y-1 mt-1">
                {authorizedPeople.map((p, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-100 rounded-lg px-3 py-1.5">
                    <span className="text-sm font-medium text-amber-900">{p.name}</span>
                    <button
                      onClick={() => handleRemoveAuthPerson(p.name)}
                      className="text-red-500 hover:text-red-700 p-0.5"
                      data-testid={`remove-auth-person-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!travelInfo && !pickupInfo && authorizedPeople.length === 0 && (
              <p className="text-sm text-amber-600 italic">No authorized people on file</p>
            )}
            {/* Add new authorized person */}
            <div className="flex gap-2 mt-3">
              <Input
                value={newAuthPerson}
                onChange={e => setNewAuthPerson(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddAuthPerson()}
                placeholder="Add name..."
                className="h-9 text-sm rounded-lg flex-1"
                data-testid="add-auth-person-input"
              />
              <Button
                onClick={handleAddAuthPerson}
                disabled={savingAuthPerson || !newAuthPerson.trim()}
                className="h-9 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 text-sm"
                data-testid="add-auth-person-btn"
              >
                {savingAuthPerson ? '...' : 'Add'}
              </Button>
            </div>
          </div>

          <button
            onClick={() => setLogType('late_arrival')}
            className="w-full bg-amber-50 hover:bg-amber-100 border-2 border-amber-300 rounded-2xl p-5 text-left transition-colors"
            data-testid="frontdesk-late-btn"
          >
            <div className="flex items-center gap-3">
              <div className="bg-amber-200 rounded-full p-3">
                <Clock className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-800">Late Arrival</p>
                <p className="text-sm text-amber-600">Record late arrival with signature</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setLogType('early_dismissal')}
            className="w-full bg-teal-50 hover:bg-teal-100 border-2 border-teal-300 rounded-2xl p-5 text-left transition-colors"
            data-testid="frontdesk-early-btn"
          >
            <div className="flex items-center gap-3">
              <div className="bg-teal-200 rounded-full p-3">
                <LogOutIcon className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <p className="text-lg font-bold text-teal-800">Early Dismissal</p>
                <p className="text-sm text-teal-600">Record early dismissal with signature</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // ── Camper List ──
  // Show selected date info (camp date, not just today)
  const campDateInfo = selectedDate
    ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : dateDisplay;
  const isOffSeason = selectedDate && selectedDate !== getEasternToday();

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <div className="bg-[#0055A4] py-4 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <h1 className="text-white text-xl font-bold font-['Fredoka']">Front Desk</h1>
          <div className="flex items-center gap-2">
            <a
              href="https://campersnapshot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
              data-testid="snapshot-link-frontdesk"
            >
              Back to Snapshot →
            </a>
            <button
              onClick={() => { setShowSettings(true); setResendKeyInput(localStorage.getItem('resend_api_key') || ''); }}
              className="text-white/80 hover:text-white p-1"
              data-testid="frontdesk-settings-btn"
              title="Email notification settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={handleRefreshRoster}
              disabled={refreshing || loading}
              className="text-white/80 hover:text-white p-1"
              data-testid="frontdesk-refresh-btn"
              title="Refresh roster"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing || loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onLogout} className="text-white/80 hover:text-white text-sm flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        </div>
        <p
          className="text-white/70 text-xs text-center mt-1 cursor-pointer hover:text-white/90 transition-colors"
          onClick={() => setShowFdDatePicker(!showFdDatePicker)}
          data-testid="frontdesk-date-toggle"
        >
          {campDateInfo} — {todayCampers.length} campers{isOffSeason ? ` (showing ${selectedDate})` : ' today'}
        </p>
        {showFdDatePicker && (
          <div className="flex justify-center mt-2" data-testid="frontdesk-date-picker">
            <input
              type="date"
              value={selectedDate || ''}
              onChange={e => {
                if (e.target.value) {
                  setSelectedDate(e.target.value);
                  setShowFdDatePicker(false);
                }
              }}
              className="border rounded-lg px-3 py-1.5 text-sm font-medium bg-white/90"
              data-testid="frontdesk-date-input"
            />
          </div>
        )}
      </div>
      <div className="max-w-lg mx-auto px-4 py-3">
        {/* Incoming dismissals fetch failure — never mistake an error for a quiet day */}
        {incomingFetchError && incomingDismissals.filter(d => !hiddenDismissalIds.has(`${d.id}_${selectedDate}`)).length === 0 && !selectedCamper && (
          <div className="mb-3 bg-red-50 border-2 border-red-300 rounded-2xl p-3 flex items-center gap-2" data-testid="incoming-dismissals-error">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm font-semibold text-red-800">Couldn't load incoming dismissals — retrying automatically</p>
          </div>
        )}

        {/* Incoming Early Dismissals from Camper Snapshot — ALWAYS at top */}
        {incomingDismissals.filter(d => !hiddenDismissalIds.has(`${d.id}_${selectedDate}`)).length > 0 && !selectedCamper && (
          <div className="mb-3 bg-orange-50 border-2 border-orange-300 rounded-2xl p-3" data-testid="incoming-dismissals-panel">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{incomingDismissals.filter(d => !hiddenDismissalIds.has(`${d.id}_${selectedDate}`)).length}</span>
              </div>
              <p className="text-sm font-bold text-orange-900">Early Dismissals Incoming</p>
            </div>
            <div className="space-y-1.5">
              {incomingDismissals.filter(d => !hiddenDismissalIds.has(`${d.id}_${selectedDate}`)).sort((a, b) => (pickupMinutes(a.dismissal_time) - pickupMinutes(b.dismissal_time)) || byLastName(a.camper_name, b.camper_name)).map(d => {
                const camper = findRosterCamper(roster, d);
                const isEditing = editingDismissalId === d.id;
                return (
                  <div key={d.id} className="bg-white rounded-xl border border-orange-200 overflow-hidden">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (isEditing) return;
                        if (camper) {
                          handleSelectCamper(camper);
                          setLogType('early_dismissal');
                          setExpectedTime(d.dismissal_time || '');
                          setSignerName(d.pickup_by || '');
                          setActiveIncomingDismissalId(d.id);
                        } else {
                          // No unique roster match — prefill search with the last
                          // name (most likely to match despite nicknames)
                          const parts = (d.camper_name || '').trim().split(/\s+/);
                          setSearch(parts.length > 1 ? parts.slice(1).join(' ') : d.camper_name);
                        }
                      }}
                      className="w-full flex items-center justify-between p-3 hover:bg-orange-50 transition-colors text-left"
                      data-testid={`incoming-dismiss-${d.id}`}
                    >
                      <div>
                        <p className="font-bold text-sm text-[#0F172A]">{lastFirst(d.camper_name)}</p>
                        <p className="text-xs text-orange-700">{d.group_code} · Pickup at {d.dismissal_time}{d.pickup_by ? ` · ${d.pickup_by}` : ''}</p>
                        <div className="flex items-center gap-1">
                          {d.scheduled_location ? (
                            <p className="text-[11px] text-[#64748B]" data-testid={`incoming-location-${d.id}`}>
                              At {d.scheduled_location}
                              {d.location_is_override && <span className="ml-1 text-amber-600 font-semibold" data-testid={`incoming-location-manual-${d.id}`}>· manual</span>}
                            </p>
                          ) : (
                            <p className="text-[11px] text-[#94A3B8] italic">Set location</p>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); editDismissalLocation(d); }}
                            className="p-0.5 text-[#94A3B8] hover:text-[#0055A4]"
                            title="Edit camper location"
                            data-testid={`edit-location-${d.id}`}
                          >
                            <Pen className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-orange-200 text-orange-800">
                          {d.source === 'admin_widget' ? 'Office' : 'App'}
                        </span>
                        {/* Edit time */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDismissalId(d.id);
                            setEditDismissalTime('');
                          }}
                          className="p-1 text-blue-400 hover:text-blue-600"
                          title="Change pickup time"
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-orange-400" />
                      </div>
                    </div>
                    {/* Edit time inline */}
                    {isEditing && (
                      <div className="flex items-center gap-2 px-3 pb-3 bg-blue-50 border-t border-blue-200" onClick={e => e.stopPropagation()}>
                        <input
                          type="time"
                          value={editDismissalTime}
                          onChange={e => setEditDismissalTime(e.target.value)}
                          className="border rounded-lg px-2 py-1.5 text-sm font-bold flex-1"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (!editDismissalTime) return;
                            const [h, m] = editDismissalTime.split(':').map(Number);
                            const ampm = h >= 12 ? 'PM' : 'AM';
                            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                            const newTime = `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
                            setIncomingDismissals(prev => prev.map(x => x.id === d.id ? {...x, dismissal_time: newTime} : x));
                            safeFetch(`${API}/summer2026/snapshot/update-dismissal-time`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                id: d.id,
                                camperName: d.camper_name,
                                groupCode: d.group_code,
                                newTime: newTime,
                                date: selectedDate,
                              }),
                            }).catch(err => console.warn('Update time:', err));
                            setEditingDismissalId(null);
                          }}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingDismissalId(null)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Early Dismissals Today Panel */}
        <div className="mb-3 bg-white rounded-xl border border-teal-200 shadow-sm overflow-hidden" data-testid="early-dismissals-panel">
          <button
            onClick={() => setEdPanelOpen(!edPanelOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-teal-50 hover:bg-teal-100 transition-colors"
            data-testid="early-dismissals-toggle"
          >
            <div className="flex items-center gap-2">
              <LogOutIcon className="w-4 h-4 text-teal-700" />
              <span className="font-bold text-sm text-teal-900">Early Dismissals Today</span>
              <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[22px] text-center">
                {earlyDismissals.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {earlyDismissals.length > 0 && (
                <>
                  <a
                    href={`${API}/summer2026/frontdesk/early-dismissals/pdf?date=${selectedDate || getEasternToday()}`}
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[10px] font-semibold rounded-lg hover:bg-red-700 transition-colors"
                    data-testid="early-dismissals-pdf-btn"
                    download
                  >
                    <FileText className="w-3 h-3" />PDF
                  </a>
                  <a
                    href={`${API}/summer2026/frontdesk/early-dismissals/csv?date=${selectedDate || getEasternToday()}`}
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-[10px] font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    data-testid="early-dismissals-csv-btn"
                    download
                  >
                    <Download className="w-3 h-3" />CSV
                  </a>
                </>
              )}
              {edPanelOpen ? <ChevronUp className="w-4 h-4 text-teal-600" /> : <ChevronDown className="w-4 h-4 text-teal-600" />}
            </div>
          </button>
          <AnimatePresence>
            {edPanelOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {edLoading && earlyDismissals.length === 0 && (
                  <div className="px-4 py-3 text-center text-xs text-teal-600">Loading...</div>
                )}
                {!edLoading && earlyDismissals.length === 0 && (
                  <div className="px-4 py-4 text-center">
                    <p className="text-xs text-[#94A3B8]">No early dismissals logged yet today</p>
                  </div>
                )}
                {earlyDismissals.length > 0 && (
                  <div className="divide-y divide-teal-100 max-h-60 overflow-y-auto">
                    {[...earlyDismissals].sort((a, b) => byLastName(a.camperName, b.camperName)).map((log, i) => (
                      <div key={log.timestamp || i} className="px-4 py-2.5 flex items-start justify-between gap-2 hover:bg-teal-50/50" data-testid={`ed-entry-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-sm text-[#0F172A]">{lastFirst(log.camperName)}</p>
                            {log.recurring && (
                              <span className="bg-teal-100 text-teal-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">REC</span>
                            )}
                          </div>
                          <p className="text-[11px] text-[#475569]">{log.bunkCode} — {log.bunkName}</p>
                          {log.notes && <p className="text-[11px] text-[#94A3B8] mt-0.5 truncate">{log.notes}</p>}
                        </div>
                        <div className="flex items-start gap-2 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xs font-medium text-teal-700">{log.time}</p>
                            {log.expectedTime && (
                              <p className="text-[10px] text-[#0F172A] font-medium mt-0.5">Pickup: {log.expectedTime}</p>
                            )}
                            <p className="text-[10px] text-[#94A3B8] mt-0.5">by {log.signerName}</p>
                          </div>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Remove early dismissal for ${log.camperName}?`)) return;
                              try {
                                const params = new URLSearchParams({ personId: log.personId, date: log.date });
                                if (log.timestamp) params.append('timestamp', log.timestamp);
                                await safeFetch(`${API}/summer2026/frontdesk/log?${params}`, { method: 'DELETE' });
                                fetchEarlyDismissals();
                              } catch (err) {
                                console.error('Delete error:', err);
                              }
                            }}
                            className="mt-0.5 p-1 rounded-lg hover:bg-red-100 text-[#94A3B8] hover:text-red-600 transition-colors"
                            data-testid={`ed-delete-${i}`}
                            title="Remove"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="frontdesk-search"
            placeholder="Search camper by name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-12 rounded-xl border-[#E2E8F0] bg-white text-base"
            autoFocus
          />
          {q && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {q && (
          <p className="text-xs text-[#475569] mb-2 px-1">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</p>
        )}

        {(loading || refreshing) && roster.length === 0 && (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#0055A4] border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-[#475569] text-sm">Loading camper roster from CampMinder...</p>
            <p className="text-[#94A3B8] text-xs mt-1">This may take a few seconds</p>
          </div>
        )}

        {!loading && !refreshing && roster.length === 0 && (
          <div className="bg-white rounded-2xl border p-8 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-[#475569] font-medium">No roster data loaded</p>
            <p className="text-xs text-[#94A3B8] mt-1">Tap refresh to sync campers from CampMinder</p>
            <button
              onClick={handleRefreshRoster}
              className="mt-3 px-4 py-2 bg-[#0055A4] text-white rounded-lg text-sm font-medium"
              data-testid="frontdesk-sync-btn"
            >
              Sync Roster Now
            </button>
          </div>
        )}

        {!loading && roster.length > 0 && todayCampers.length === 0 && (
          <div className="bg-white rounded-2xl border p-8 text-center">
            <p className="text-[#475569] font-medium">No camp today</p>
            <p className="text-xs text-[#94A3B8] mt-1">Campers appear on scheduled camp days (Jun 29 - Aug 20)</p>
          </div>
        )}

        <div className="space-y-2">
          {filtered.map(c => {
            const si = SESSION_COLORS[c.sessionType] || SESSION_COLORS.full;
            const today = selectedDate || getEasternToday();
            const pickupPill = localStorage.getItem(`pickup_${c.personId}_${today}`);
            return (
              <button
                key={c.personId}
                onClick={() => handleSelectCamper(c)}
                className="w-full bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-3 text-left hover:border-[#0055A4] transition-colors"
                data-testid={`frontdesk-camper-${c.personId}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CamperAvatar firstName={c.firstName} lastName={c.lastName} personId={c.personId} size={38} />
                    <div>
                      <p className="font-semibold text-[#0F172A]">{c.lastName}, {c.firstName}</p>
                      <p className="text-xs text-[#475569]">{c.bunkCode} — {c.bunkName}</p>
                      {pickupPill && (
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#0F172A] text-white" data-testid={`pickup-pill-${c.personId}`}>
                          Pickup: {pickupPill}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${si.bg} ${si.text}`}>
                    {si.label}
                  </span>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && !loading && roster.length > 0 && todayCampers.length > 0 && (
            <div className="text-center py-8 text-[#94A3B8]">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p>No campers found</p>
            </div>
          )}
        </div>
      </div>

      {/* Resend API Key Settings Overlay */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-testid="frontdesk-settings-overlay">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">Notification Settings</h2>
              <button onClick={() => setShowSettings(false)} className="text-[#94A3B8] hover:text-[#475569]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1">Resend API Key</label>
              <div className="relative">
                <Input
                  data-testid="frontdesk-resend-key-input"
                  type={showResendKey ? 'text' : 'password'}
                  value={resendKeyInput}
                  onChange={e => setResendKeyInput(e.target.value)}
                  placeholder="re_..."
                  className="h-12 text-base rounded-xl pr-20"
                />
                <button
                  type="button"
                  onClick={() => setShowResendKey(!showResendKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#0055A4] font-medium"
                  data-testid="frontdesk-resend-key-toggle"
                >
                  {showResendKey ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="text-xs text-[#94A3B8] mt-1">Required for sending head counselor email notifications via Resend.</p>
            </div>
            <Button
              onClick={() => {
                localStorage.setItem('resend_api_key', resendKeyInput);
                setShowSettings(false);
              }}
              className="w-full h-12 bg-[#0055A4] hover:bg-[#003F7D] text-white rounded-xl font-bold"
              data-testid="frontdesk-resend-key-save"
            >
              <Save className="w-4 h-4 mr-2" />
              Save API Key
            </Button>
            {localStorage.getItem('resend_api_key') && (
              <p className="text-xs text-emerald-600 text-center">API key is configured</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
